import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Lock, Check, User, Mail, Sun, Moon, Contrast, RotateCcw } from "lucide-react";
import axios from "axios";
import { useDisplay } from "@/context/DisplayContext";
import BankConnection from "@/components/BankConnection";

export default function SettingsPage({ user, api, onRefresh }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [displayName, setDisplayName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);

  const { fontSize, zoom, theme, compact, setFontSize, setZoom, setTheme, setCompact, resetAll } = useDisplay();

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (newPassword !== confirmPassword) { toast.error("Passwords do not match"); return; }
    setSaving(true);
    try {
      await axios.post(`${api}/auth/change-password`, { current_password: currentPassword, new_password: newPassword });
      toast.success("Password changed successfully");
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to change password"); }
    setSaving(false);
  };

  const handleUpdateName = async (e) => {
    e.preventDefault();
    if (!displayName.trim()) { toast.error("Name cannot be empty"); return; }
    setSavingName(true);
    try {
      await axios.put(`${api}/auth/profile`, { name: displayName.trim() });
      toast.success("Profile updated");
      if (onRefresh) onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update profile"); }
    setSavingName(false);
  };

  const themes = [
    { id: "light", label: "Light", icon: Sun },
    { id: "dark", label: "Dark", icon: Moon },
    { id: "high-contrast", label: "High Contrast", icon: Contrast },
  ];

  return (
    <div data-testid="settings-page" className="space-y-8">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-gray-500 mt-1">Manage your account, display, and accessibility preferences</p>
      </div>

      {/* Bank Connection (TrueLayer Open Banking) */}
      <BankConnection api={api} />

      {/* Display Preferences */}
      <div className="border border-gray-200 rounded-sm">
        <div className="px-5 py-4 border-b border-gray-200 bg-gray-50">
          <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-gray-600">Display & Accessibility</h2>
        </div>
        <div className="p-5 space-y-6">
          {/* Font Size */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Text Scaling</Label>
              <span className="font-mono-data text-sm font-semibold" data-testid="settings-font-value">{fontSize}%</span>
            </div>
            <Slider
              data-testid="settings-font-slider"
              value={[fontSize]}
              onValueChange={(v) => setFontSize(v[0])}
              min={75} max={125} step={5}
            />
            <div className="flex justify-between text-xs text-gray-400 font-mono-data mt-1">
              <span>A-</span><span>100%</span><span>A+</span>
            </div>
          </div>

          {/* Zoom */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Page Zoom</Label>
              <span className="font-mono-data text-sm font-semibold" data-testid="settings-zoom-value">{zoom}%</span>
            </div>
            <Slider
              data-testid="settings-zoom-slider"
              value={[zoom]}
              onValueChange={(v) => setZoom(v[0])}
              min={75} max={125} step={5}
            />
            <div className="flex justify-between text-xs text-gray-400 font-mono-data mt-1">
              <span>75%</span><span>100%</span><span>125%</span>
            </div>
          </div>

          {/* Theme — Segmented Control */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2 block">Theme</Label>
            <div className="inline-flex rounded-sm border border-gray-200 overflow-hidden">
              {themes.map((t) => {
                const Icon = t.icon;
                const active = theme === t.id;
                return (
                  <button
                    key={t.id}
                    data-testid={`theme-${t.id}`}
                    onClick={() => setTheme(t.id)}
                    className={`flex items-center gap-2 px-5 py-2.5 text-sm transition-all border-r border-gray-200 last:border-r-0 ${
                      active
                        ? t.id === "dark" ? "bg-gray-800 text-white" :
                          t.id === "high-contrast" ? "bg-black text-yellow-400 border-yellow-400" :
                          "bg-gray-900 text-white"
                        : "bg-white hover:bg-gray-50 text-gray-600"
                    }`}
                  >
                    <Icon className="w-4 h-4" strokeWidth={1.5} />
                    <span className="hidden sm:inline">{t.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Compact Mode */}
          <div className="flex items-center justify-between border border-gray-200 rounded-sm p-4 bg-gray-50">
            <div>
              <Label className="text-sm font-medium text-gray-700">Compact View</Label>
              <p className="text-xs text-gray-500 mt-0.5">Reduce spacing and font sizes to fit more content on screen</p>
            </div>
            <Switch
              data-testid="settings-compact-switch"
              checked={compact}
              onCheckedChange={setCompact}
            />
          </div>

          {/* Reset */}
          <div className="flex justify-end pt-2">
            <button data-testid="settings-reset-display" onClick={resetAll} className="text-xs text-gray-400 hover:text-gray-600 transition-colors border border-gray-200 rounded-sm px-3 py-1.5 hover:bg-gray-50 flex items-center gap-1.5">
              <RotateCcw className="w-3 h-3" strokeWidth={1.5} />
              Reset to defaults
            </button>
          </div>
        </div>
      </div>

      {/* Profile */}
      <div className="border border-gray-200 rounded-sm">
        <div className="px-5 py-4 border-b border-gray-200 bg-gray-50">
          <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-gray-600">Profile</h2>
        </div>
        <form onSubmit={handleUpdateName} className="p-5 space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Email</Label>
            <div className="relative mt-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" strokeWidth={1.5} />
              <Input data-testid="settings-email" value={user?.email || ""} disabled className="pl-10 rounded-sm bg-gray-50 text-gray-500 cursor-not-allowed" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Display Name</Label>
            <div className="relative mt-1">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" strokeWidth={1.5} />
              <Input data-testid="settings-name-input" value={displayName} onChange={(e) => setDisplayName(e.target.value)} className="pl-10 rounded-sm" placeholder="Your name" />
            </div>
          </div>
          <Button data-testid="settings-save-profile-btn" type="submit" disabled={savingName} className="rounded-sm bg-black text-white hover:bg-gray-800 gap-2">
            <Check className="w-4 h-4" strokeWidth={1.5} />{savingName ? "Saving..." : "Save Profile"}
          </Button>
        </form>
      </div>

      {/* Change Password */}
      <div className="border border-gray-200 rounded-sm">
        <div className="px-5 py-4 border-b border-gray-200 bg-gray-50">
          <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-gray-600">Change Password</h2>
        </div>
        <form onSubmit={handleChangePassword} className="p-5 space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Current Password</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" strokeWidth={1.5} />
              <Input data-testid="settings-current-password" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="pl-10 rounded-sm" placeholder="Enter current password" required />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">New Password</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" strokeWidth={1.5} />
              <Input data-testid="settings-new-password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="pl-10 rounded-sm" placeholder="Min 6 characters" required />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Confirm New Password</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" strokeWidth={1.5} />
              <Input data-testid="settings-confirm-password" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="pl-10 rounded-sm" placeholder="Repeat new password" required />
            </div>
          </div>
          <Button data-testid="settings-change-password-btn" type="submit" disabled={saving} className="rounded-sm bg-black text-white hover:bg-gray-800 gap-2">
            <Lock className="w-4 h-4" strokeWidth={1.5} />{saving ? "Changing..." : "Change Password"}
          </Button>
        </form>
      </div>
    </div>
  );
}
